import './tree-view.css';

import { ArrowDownIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import cx from 'classnames';
import { isEmpty, replaceOrAppend } from 'radash';
import { useEffect, useState } from 'react';
import TreeView, { INode, NodeId } from 'react-accessible-treeview';
import { IFlatMetadata } from 'react-accessible-treeview/dist/TreeView/utils';

import { IconButton } from '@/components/core/IconButton';
import { Loader } from '@/components/core/Loader';
import { logger } from '@/services/logger';
import { restUrl } from '@/services/utils';
import { AdditionalCategory, DataModelAttribute } from '@/types/DataModel';

interface LazyLoadingTreeViewProps {
    additionalDataModelName: string;
    attribute: DataModelAttribute;
    extension?: string;
    initialData: AdditionalCategory[];
    onSelect: (node: { label: string; value: string }) => void;
    selectedCategoryCode?: string;
}

// Normalize value for case-insensitive comparison
const compareCategoryValue = (value?: string | null) => (value ?? '').toLocaleLowerCase();

// Sort categories by title, then name, then code
const sortAdditionalCategories = (categories: AdditionalCategory[]) => {
    return [...categories].sort((left, right) => {
        const titleCompare = compareCategoryValue(left.title).localeCompare(compareCategoryValue(right.title));
        if (titleCompare !== 0) {
            return titleCompare;
        }

        const nameCompare = compareCategoryValue(left.name).localeCompare(compareCategoryValue(right.name));
        if (nameCompare !== 0) {
            return nameCompare;
        }

        return compareCategoryValue(left.code).localeCompare(compareCategoryValue(right.code));
    });
};

const LazyLoadingTreeView = ({
    additionalDataModelName,
    attribute,
    extension,
    initialData,
    onSelect,
    selectedCategoryCode
}: LazyLoadingTreeViewProps) => {

    const [isReady, setIsReady] = useState(false);
    const [allNodes, setAllNodes] = useState<INode<IFlatMetadata>[] | null>(null);
    const [defaultExpandedNodes, setDefaultExpandedNodes] = useState<NodeId[]>([]);

    const mapCategoriesToNodes = (data: AdditionalCategory[]): INode[] => {
        return sortAdditionalCategories(data).map(node => {
            return {
                id: node.id,
                name: `${node.title} [${node.code}]`,
                isBranch: !node.leaf,
                children: [],
                parent: node.parent,
                title: node.title,
                metadata: {
                    code: node.code // In order to access it in the `nodeRenderer` function
                },
            };
        });
    };

    const loadCategoryPaths = async (allNodes: INode[]) => {

        try {
            // FIXME: replace with new endpoints:
            // - GET datamodel/categories/{nameOrCode}?serviceName=...&dataModel=...
            // - GET datamodel/categories/{nameOrCode}/children?serviceName=...&dataModel=...
            // - GET datamodel/categories/{nameOrCode}/ancestors?serviceName=...&dataModel=...
            // - GET datamodel/categories?serviceName=...&dataModel=...&q=...
            // - POST datamodel/categories?serviceName=...&dataModel=... BODY [String]
            // - POST datamodel/categories/children?serviceName=...&dataModel=... BODY [String]
            const extensionQuery = !isEmpty(extension) ? `&extension=${extension}`: '';
            const categoryPathResponse = await axios.get<AdditionalCategory[]>(
                restUrl(`datamodel/categoryPath/${selectedCategoryCode}?dataModel=${additionalDataModelName}${extensionQuery}`)
            );
            const categoryPath = categoryPathResponse.data;

            if (isEmpty(categoryPath)) {
                throw new Error('AdditionalCategoryTreeView: Selected categroy path is empty');
            }

            // Remove the parent root category (the last one) as
            // we already have it (loaded via initial data)
            const rootCategory = categoryPath.pop() as AdditionalCategory;

            // FIXME: replace with new endpoints:
            // - GET datamodel/categories/{nameOrCode}?serviceName=...&dataModel=...
            // - GET datamodel/categories/{nameOrCode}/children?serviceName=...&dataModel=...
            // - GET datamodel/categories/{nameOrCode}/ancestors?serviceName=...&dataModel=...
            // - GET datamodel/categories?serviceName=...&dataModel=...&q=...
            // - POST datamodel/categories?serviceName=...&dataModel=... BODY [String]
            // - POST datamodel/categories/children?serviceName=...&dataModel=... BODY [String]
            // For each category in the path, let's load it's children
            // so we can construct a helpful expanded tree for the user
            const parentCategoriesQuery = categoryPath.map(category => `&parent=${category.parent}`).join('');
            const parentCategoriesResponse = await axios.get<AdditionalCategory[]>(
                restUrl(`datamodel/retrieval/categories?limit=500&dataModel=${additionalDataModelName}${extensionQuery}${parentCategoriesQuery}`)
            );
            const parentCategories = parentCategoriesResponse.data;

            // Map the categories to children nodes for the tree component, and
            // fill the list of children ids
            let nodes = mapCategoriesToNodes([...parentCategories, rootCategory]);
            nodes = nodes.map(node => ({
                ...node,
                children: nodes
                    .filter(childNode => childNode.parent === node.id)
                    .map(childNode => childNode.id),
            }));

            // Set the expanded nodes, i.e. expand all the parent nodes loaded using the path
            setDefaultExpandedNodes(
                nodes.map(node => node.id)
            );

            // Merge the loaded parent nodes to the root nodes initially loaded, replacing
            // the root nodes with the one with the updated children ids.
            const mergedNodes = nodes.reduce((acc, current) => {

                const alreadyExistingNode = acc.find(node => node.id === current.id);
                return replaceOrAppend(acc, current, node => node.id === alreadyExistingNode?.id);

            }, allNodes);

            setAllNodes(mergedNodes);

        } catch(exception) {
            logger.error('AdditionalCategoryTreeView: Could not load selected category path', exception);
        }

        setIsReady(true);

    };

    // Update data when initial data changes
    useEffect(() => {

        const rootNodes = mapCategoriesToNodes(initialData);

        // We need a root node to align with the structure of react-accessible-treeview, so
        // root node id is the parent id of one of the child nodes
        let parentRootNodesId: NodeId = 0;
        if (!isEmpty(rootNodes) && rootNodes[0].parent !== null) {
            parentRootNodesId = rootNodes[0].parent;
        }

        const rootNode = {
            children: rootNodes.map(node => node.id),
            id: parentRootNodesId,
            name: '',
            parent: null
        };
        rootNodes.push(rootNode);

        setAllNodes(rootNodes);

        // We load the categories that construct the path of the selected category (if any), otherwise
        // we are done
        if (!isEmpty(selectedCategoryCode)) {
            loadCategoryPaths(rootNodes);
        } else {
            setIsReady(true);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Handle node selection
    const handleNodeSelect = ({ element, isSelected }: any) => {
        if (isSelected) {
            onSelect({
                label: element.name,
                value: element.metadata.code,
            });
        }
    };

    const wrappedOnLoadData = async (element: INode<IFlatMetadata>) => {
        const nodeHasNoChildData = element.children.length === 0;
        if (nodeHasNoChildData) {
            await onLoadData(element);
        }
    };

    const onLoadData = async (element: INode<IFlatMetadata>) => {
        try {
            // FIXME: Use PROPER code for REST requests and NOT STRINGS!!!
            const response = await axios.get<AdditionalCategory[]>(
                restUrl(
                    `datamodel/retrieval/categories?dataModel=${
                        attribute.params?.additionalModule ?? ''
                    }&extension=${
                        attribute.params?.extension ?? ''
                    }&limit=100&&parent=${element.id}`,
                )
            );
            const preparedChildren = mapCategoriesToNodes(response.data);
            if (preparedChildren.length > 0 && preparedChildren[0].parent) {
                setAllNodes((prevData) =>
                    updateTreeData(
                        prevData,
                        element.id,
                        preparedChildren,
                    ),
                );
            }
        } catch (error) {
            logger.error('AdditionalCategoryTreeView: Error during loading of tree: ',error);
        }
    };

    const updateTreeData: any = (
        list: INode<IFlatMetadata>[],
        parentId:string,
        children: INode<IFlatMetadata>[],
    ) => {
        const data = list.map((node) => {
            if (node.id === parentId) {
                node.children = children.map((el) => {
                    return el.id;
                });
            }
            return node;
        });
        return data.concat(children);
    };

    // Render loading state
    if (!allNodes || allNodes.length === 0 || !isReady) {
        return <Loader />;
    }

    return (
        <div className="treeview tw-w-full tw-h-full">
            <TreeView
                data={allNodes}
                aria-label="Tree view"
                defaultExpandedIds={defaultExpandedNodes}
                onLoadData={(children) => wrappedOnLoadData(children.element)}
                onNodeSelect={handleNodeSelect}
                propagateSelect
                togglableSelect
                propagateSelectUpwards
                nodeRenderer={({
                    element,
                    isBranch,
                    isExpanded,
                    getNodeProps,
                    level,
                    handleSelect,
                    handleExpand,
                }) => (
                    <TreeNode
                        element={element}
                        isBranch={isBranch}
                        isExpanded={isExpanded}
                        isSelected={selectedCategoryCode === element.metadata?.code}
                        getNodeProps={getNodeProps}
                        level={level}
                        handleSelect={handleSelect}
                        handleExpand={handleExpand}
                    />
                )}
            />
        </div>
    );
};

interface TreeNodeProps {
    element: INode<IFlatMetadata>;
    isBranch: boolean;
    isExpanded: boolean;
    isSelected: boolean;
    getNodeProps: () => any;
    level: number;
    handleSelect: (e: React.MouseEvent) => void;
    handleExpand: (e: React.MouseEvent) => void;
}

// Component for rendering tree nodes
const TreeNode = ({
    element,
    isBranch,
    isExpanded,
    isSelected,
    getNodeProps,
    level,
    handleSelect,
    handleExpand,
}: TreeNodeProps) => {
    const branchNode =
        isBranch &&
        (isExpanded && element.children.length === 0 ? (
            <Loader aria-hidden className="loading-icon" />
        ) : (
            <ArrowIcon isOpen={isExpanded} />
        ));

    return (
        <div
            {...getNodeProps()}
            className={`tw-rounded-md tw-flex tw-items-center tw-h-9 tw-bg-gray-100 tw-my-1 tw-cursor-pointer ${isSelected ? 'tw-bg-gray-300': ''}`}
            style={{ marginLeft: 30 * (level - 1) }}
            onClick={(e) => {
                !isBranch && handleSelect(e);
                handleExpand(e);
                e.stopPropagation();
            }}
        >
            {branchNode}
            <span className="name tw-leading-none tw-ml-2">{element.name}</span>
        </div>
    );
};

// Arrow icon component
const ArrowIcon = ({ isOpen }: { isOpen: boolean }) => {
    const baseClass = 'tw-cursor-pointer tw-mb-0.5 tw-select-none tw-w-9 tw-h-9 tw-flex tw-items-center tw-justify-center tw-border-0 tw-rounded-md tw-bg-gray-200 tw-text-xs';
    const classes = cx(baseClass, {
        ['tw--rotate-90 tw-transform tw-origin-center']: !isOpen,
    });

    return (
        <IconButton className={classes} icon={ArrowDownIcon} />
    );
};

export default LazyLoadingTreeView;
